<template>
    <div upload question :id="`question-${questionData.number}`">
        <questionheader :questionData="questionData" />
        <div data-question-main>
            <template v-for="(answer,index) in questionData.answers">
                <div v-if="answer.type !== `OuterField`" data-answer :key="index">
                    <div data-input-label>
                        <label
                                :for="`question-${questionData.number}_answer-${answer.number}_file-upload`"
                            >{{answer.label}}</label>
                    </div>
                    <div data-file-upload-control :disabled="!editingAllowed">
                        <label
                            :for="`question-${questionData.number}_answer-${answer.number}_file-upload`"
                            data-button
                            :aria-controls="`question-${questionData.number}_answer-${answer.number}_file-name`"
                            type="button"
                            tabindex="0"
                            :disabled="state.global.disableNavButtons"
                            @keydown.enter="$refs[`file-upload-${questionData.number}`].click()"
                        >
                            <p>{{get.textResource(`rs_${answer.type.toLowerCase()}_upload_label`)}}</p>
                        </label>
                        <input
                            type="file"
                            :id="`question-${questionData.number}_answer-${answer.number}_file-upload`"
                            @change="AJAXSubmitProgress($event, answer)"
                            data-only-screenreader
                            :accept="$data[`acceptedExtensions${answer.type}`]"
                            :ref="`file-upload-${questionData.number}`"
                            tabindex="-1"
                            capture
                            :multiple="questionData.maxUploadsPerAnswer > 1"
                        />
                        <span data-file-name>
                            <p
                                :id="`question-${questionData.number}_answer-${answer.number}_file-name`"
                            >{{uploadLabel}}</p>
                        </span>
                    </div>
                    <div data-progress-container :data-show="answer.showLoader">
                        <label :for="`question-${questionData.number}_file-upload`">{{answer.progress}}%</label>
                        <progress
                            :id="`question-${questionData.number}_file-upload`"
                            max="100"
                            :value="answer.progress"
                        >{{answer.progress}}%</progress>
                    </div>
                    <strong
                        aria-live="assertive"
                        :id="`question-${questionData.number}_answer-${answer.number}_user-message`"
                    >{{answer.userMessage}}</strong>
                    <div v-if="answer.uploads" data-file-list>
                        <ul>
                            <li v-for="(file, index) in answer.uploads" :key="index">
                                <font-awesome-icon
                                    @click="removeFileBtnClicked(answer, file)"
                                    @keyup.enter="removeFileBtnClicked(answer, file)"
                                    tabindex="0"
                                    data-remove-file
                                    icon="trash"
                                    aria-hidden="true"
                                    :title="get.textResource(`rs_upload_remove_file_button_title`)"
                                    :id="`question-${questionData.number}_answer-${file.id}_delete-icon`"
                                />
                                {{file.fileName}}
                            </li>
                        </ul>
                    </div>
                </div>
            </template>
            <checkboxradio
                v-if="outerField !== undefined"
                :questionData="questionData"
                :outerfield="true"
                :answerData="outerField"
                :showLabel="true"
                :disabled="!get.editingAllowed(questionData, false) || state.global.showLoader"
                type="checkbox"
                @element-click="outerFieldClicked"
            />
        </div>
        <questionfooter :questionData="questionData" />
        <validationpopup :questionValidation="questionValidation" />
    </div>
</template>

<script>
import mixins from "@/global/mixins";
import Vue from "vue";

export default {
    mixins: [mixins.defaultQuestion],
    data(){
        return {
            acceptedExtensionsFile: `audio/*, video/*, image/png, image/jpeg, image/jpg, image/gif, .xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, .doc, .docx, application/vnd.openxmlformats-officedocument.wordprocessingml.document, .ppt, application/vnd.openxmlformats-officedocument.presentationml.presentation, .odp, text/csv, application/csv, .csv, application/vnd.ms-excel, .odt, .ods, application/vnd.oasis.opendocument.presentation, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.oasis.opendocument.spreadsheet, application/vnd.oasis.opendocument.text, text/plain, application/vnd.ms-powerpoint, application/msword, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/pdf`,
            acceptedExtensionsImage: `image/png, image/jpeg, image/jpg, image/gif`,
            acceptedExtensionsVideo: `video/*`,
            acceptedExtensionsAudio: `audio/*`,
        }
    },
    computed: {
        answersGiven() {
            return this.valueMissing ? 0 : 1;
        },
        uploadsComplete(){
            return this.questionData.answers.some(answer => answer.uploads !== null && answer.uploads !== undefined && answer.uploads?.length > 0);
        },
        outerField() {
            return this.questionData.answers.find((answer) => answer.type === `OuterField`);
        },
        valueMissing() {
            return this.restrictions.required === true && !this.uploadsComplete && (this.outerField === undefined || !this.outerField.isChecked);
        },
        editingAllowed() {
            return this.get.editingAllowed(this.questionData);
        },
        uploadLabel(){
            const maxUploadText = this.get.textResource(`rs_upload_max_files_and_size`);
            
            if(maxUploadText) return maxUploadText.replace(`[x]`, this.questionData.maxUploadsPerAnswer);
            else return ``;
        }
    },
    methods: {
        async removeFileBtnClicked(answer, file) {
            answer.userMessage = this.get.textResource(`rs_file_remove_started`);

            await fetch(`/v1/FileUpload/DeleteFile`, {
                method: `post`,
                headers: new Headers({ 
                    "content-type": "application/json", 
                    Authorization: "bearer " + this.state.surveyInfo.token,
                    CTSID: this.$store.state.surveyInfo.surveyId
                }),
                body: JSON.stringify({
                    fileId: file.id,
                    respondentId: sessionStorage.getItem(`respondentId`),
                    surveyId: sessionStorage.getItem(`surveyId`),
                }),
            }).then((res) => {
                if (res.status === 204) {
                    answer.userMessage = this.get.textResource(`rs_rs_file_remove_succesful`);
                    answer.uploads = answer.uploads.filter(f => f.id !== file.id);

                    if(answer.uploads.length === 0) {
                        answer.showLoader = false;
                        answer.progress = 0;
                        this.resetFileUpload(answer);
                    }
                } else {
                    answer.userMessage = this.get.textResource(`rs_rs_file_delete_failed`);
                }
            });
        },
        changeNavButton(newValue){
            this.$store.commit(`changeGlobal`, {
                variableName: `disableNavButtons`,
                newValue: newValue
            });
        },
        fileTypeIsAccepted(file, answer){
            const fileType = file.type.split(`/`).shift();
           
            if (file.type == 'image/svg+xml') return false 
            
            return [`image`, `audio`, `video`].includes(fileType) || this[`acceptedExtensions${answer.type}`].split(`, `).includes(file.type)
        },
        AJAXSubmitProgress: function (event, answer) {
            const maxUploadAmt = this.questionData.maxUploadsPerAnswer;
            let formdata = new FormData();
            let files = Array.from(event.target.files); 
            let allFilesAreValid = true;

            formdata.append(`questionId`, this.questionData.id);
            formdata.append(`answerId`, answer.id);
            formdata.append(`surveyId`, sessionStorage.getItem(`surveyId`));
            formdata.append(`respondentId`, sessionStorage.getItem(`respondentId`));  
            
            let countTotalFiles = files.length + answer.uploads?.length;

            if(files.length > maxUploadAmt || answer.uploads?.length >= maxUploadAmt || countTotalFiles > maxUploadAmt) {
                answer.userMessage = this.get.textResource(`rs_too_many_files_uploaded`).replace(`[x]`, maxUploadAmt);
                return;
            }

            files.forEach((file, index) => { 
                    if(allFilesAreValid) {
                        if (this.fileTypeIsAccepted(file, answer) === false) {
                            answer.userMessage = this.get.textResource(`rs_file_invalid_extension`);
                            allFilesAreValid = false;
                        } else {
                            // 50mb
                            if (file.size > 52428800) {
                                answer.userMessage = this.get.textResource(`rs_file_too_large`);
                                allFilesAreValid = false;
                            } else {
                                answer.userMessage = this.get.textResource(`rs_file_download_started`);
                            }

                            if(answer.uploads !== null && answer.uploads.length > 0) 
                            {
                                answer.uploads.forEach((file, index) => {
                                    formdata.append(`file${index}`, file);
                                });
                            }
                           
                            formdata.append(`file${index}`, file); 
                        }
                    }
            });
    
            if(allFilesAreValid === false) return;
            
            answer.showLoader = true;

            this.changeNavButton(true);

            const ajax = new XMLHttpRequest();
            
            ajax.upload.addEventListener(`progress`, (e) => {this.progressHandler(e, answer)});
            ajax.addEventListener(`load`, (e) => {this.completeHandler(e, answer)});
            ajax.addEventListener(`error`, (e) => {this.errorHandler(answer, e)});
            ajax.addEventListener(`abort`, (e) => {this.errorHandler(answer, e)});
            ajax.open(`POST`, `/v1/FileUpload/StreamUpload`);
            ajax.setRequestHeader(`Authorization`, `bearer ${this.state.surveyInfo.token}`);
            ajax.send(formdata);
        },
        resetFileUpload: function (answer) {
            this.$refs[`file-upload-` + this.questionData.number][this.questionData.answers.findIndex(a => a.id === answer.id)].value = ``;
            this.changeNavButton(false);
            answer.showLoader = false;
            answer.progress = 0;
        },
        outerFieldClicked() {
            this.outerField.isChecked = !this.outerField.isChecked;
        },
        progressHandler: function (event, answer) {
            answer.progress = Math.round((event.loaded / event.total) * 100);
            //  Despite the progress being 100%, the file is not truly 100% until we receive a 200 status code from the server
            if(answer.progress > 90) answer.progress = 90;
        },
        completeHandler: function (event, answer) {
            const response = JSON.parse(event.target.response);
    
            if(response.error) {
                this.errorHandler(answer);
                return;
            }

            if (answer.uploads === null) {
                answer.uploads = [];
            }
              
            response.uploadedFiles.forEach((file) => {
               let id = file.id;
               let fileName = file.fileName;

                answer.uploads.push({ id, fileName });
            });   

            answer.showLoader = true;
            answer.userMessage = this.get.textResource(`rs_file_upload_succesful`);
            answer.progress = 100;
            
            this.changeNavButton(false);
        },
        errorHandler: function (answer) {
            answer.userMessage = this.get.textResource(`rs_file_upload_failed`);
            this.resetFileUpload(answer);
        },
    },
    created(){
        this.questionData.answers.forEach(answer => {
            Vue.set(answer, `userMessage`, ``);
            Vue.set(answer, `progress`, 0);
        });
    }
};
</script>

<style lang="scss" scoped>
progress {
    border-radius: 13px;
    height: 22px;
    width: 100%;
}
progress[value] {
  -webkit-appearance: none;
     -moz-appearance: none;
          appearance: none;
  border: none;
}
progress::-moz-progress-bar {
    background-image: -moz-linear-gradient(
      left, 
      var(--primary-color), 
      var(--primary-color)
    );
}
progress::-webkit-progress-bar {
    border-radius: 13px;
    background-color: #e4e4e4;
}
progress[value]::-moz-progress-bar {
    border-radius: 13px;
    background-color: #e4e4e4;
}

progress::-webkit-progress-value {
    background-color: var(--primary-color);
    border-radius: 13px;
}
progress::-moz-progress-value {
    background-color: var(--primary-color);
    border-radius: 13px;
}

[data-file-list] {
    align-self: flex-start;

    ul {
        display: flex;
        flex-direction: column;
        padding: 0;
    }    

    li {
        padding: 5px 0;
        display: flex;
        font-size: 14px;
        align-items: center;
    }
}

[data-show-loader]:after {
    content: "";
    transition: all;
}

[data-question-main] {
    justify-content: center;
    align-items: center;
}

[data-answer] {
    max-width: 500px;
    flex-direction: column;
    
    ~ [data-answer] {
        padding-top: 50px;
    }
}

[data-input-label] {
    margin: 10px 0;
    display: flex;
    justify-content: flex-start;
    width: 100%;
    align-items: center;
}

[data-progress-container] {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    height: 0;
    transition: 0.3s ease-out;
    opacity: 0;
    margin-top: 1px;

    &[data-show] {
        margin-top: 30px;
        height: 40px;
        opacity: 1;
    }

    label {
        font-size: 17px;
        position: absolute;
        color: #fff;
    }
}

[data-file-upload-control] {
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: stretch;
    min-height: 45px;
    transition: 0.4s;

    p {
        margin: 0;
        font-size: 14px;
    }
}

[data-outerfield] {
    margin-top: 20px;
}

[disabled] {
    pointer-events: none;

    [data-button],
    [data-file-name] {
        border-color: #e6e6e6;
        background: #f8f8f8;
        color: #909090;
    }
}

[data-button] {
    background-color: var(--primary-color);
    color: #fff;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    padding-left: 20px;
    padding-right: 20px;
    height: auto;
    width: auto;
    min-width: 180px;
    
    p {
        color: #fff;
    }
}

[data-file-name] {
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
    border: 2px solid #333;
    margin-left: -2px;
    height: auto;
    flex-basis: 400px;
    flex-grow: 1;
    flex-shrink: 1;
    display: flex;
    align-items: center;
    width: calc(100% - 100px);
    overflow: hidden;

    p {
        padding: 5px 15px;
        width: 100%;
    }
}

strong {
    margin-top: 10px;
    width: 100%;
}

[data-remove-file] {
    font-size: 18px;
    cursor: pointer;
    margin-right: 10px;
    height: 25px;

    &:hover {
        transition: 0.4s;
        transform: scale(1.15);
    }
}
</style>